import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, getDispatcherToken } from './DispatcherApp';
import { criticalChangeSummary, cancelConfirmation, showOperationFeedback } from './scheduleOperation';

import { OPTIONAL_TEXT_FIELDS, COUNT_FIELDS, validateScheduleForm, confirmScheduleConflict } from './scheduleForm';

const SERVICE_TYPES = [
  { value: 'transfer', label: '接送' },
  { value: 'charter', label: '包车' },
  { value: 'other', label: '其他' },
];

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );

  const ReadOnly = ({ label, value }) => (
    <Field label={label}>
      <p className="text-sm text-foreground py-1.5 min-h-[32px]" style={{overflowWrap:'anywhere'}}>{value ?? '-'}</p>
    </Field>
  );

  const Input = ({ label, field, form, setForm, canEdit, type = 'text', placeholder = '' }) => (
    <Field label={label}>
      {canEdit ? (
        <input
          type={type}
          aria-label={label}
          min={type === 'number' ? 0 : undefined}
          max={field === 'infant_seat_count' ? 10 : undefined}
          step={type === 'number' ? (field.includes('amount') && form[field === 'price_amount' ? 'price_currency' : 'driver_collection_currency'] === 'CNY' ? '0.01' : '1') : undefined}
          value={form[field] ?? ''}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          placeholder={placeholder}
          className="premium-input text-sm"
          data-testid={`edit-${field}`}
        />
      ) : (
        <p className="text-sm text-foreground py-1.5">{form[field] ?? '-'}</p>
      )}
    </Field>
  );


export default function EditScheduleModal({ schedule, drivers, dispatcher, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [initialForm, setInitialForm] = useState({});
  const [revision, setRevision] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const busy = useRef(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditError, setAuditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('detail'); // detail | logs
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isOwnExtra = schedule?.source_type === 'dispatcher_extra' && 
    schedule?.created_by_id === dispatcher?.id;
  const canEdit = dispatcher?.permissions?.can_edit && isOwnExtra && schedule?.status === 'active';
  const canCancel = dispatcher?.permissions?.can_cancel && isOwnExtra && schedule?.status === 'active';
  const canDelete = dispatcher?.permissions?.can_cancel && isOwnExtra;
  const canViewPrice = !!dispatcher?.permissions?.can_view_price && !(dispatcher?.hidden_price_driver_ids || []).includes(schedule.driver_id);
  const canViewCustomer = !!dispatcher?.permissions?.can_view_customer_info;
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const requestClose = () => {
    if (busy.current) return;
    if (dirty && !window.confirm('修改尚未保存，确定放弃修改并关闭吗？')) return;
    onClose();
  };
  useEffect(() => {
    const guard = event => {
      if (dirty || saving) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty, saving]);

  const fetchAuditLogs = useCallback(async (hydrate = false) => {
    setAuditError('');
    try {
      const res = await fetch(`${API_BASE_URL}/dispatcher/schedule/${schedule.id}/detail`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.audit_logs || []);
        if (hydrate === true && data.schedule) {
          setRevision(data.schedule.revision ?? 0);
          setUpdatedAt(data.schedule.updated_at ?? null);
          setForm(current => {
            const snapshot = Object.fromEntries(Object.keys(current).map(key => [key, data.schedule[key] ?? (key.endsWith('currency') ? 'JPY' : key === 'service_type' ? 'transfer' : '')]));
            setInitialForm(snapshot);
            return snapshot;
          });
        }
      } else { setAuditError('操作记录加载失败，请重试'); }
    } catch (err) {
      setAuditError('操作记录加载失败，请重试');
      console.error('Failed to fetch audit logs:', err);
    }
  }, [schedule.id]);

  useEffect(() => {
    const snapshot = {
      date: schedule.date || '',
      time: schedule.time || '',
      pickup_location: schedule.pickup_location || '',
      dropoff_location: schedule.dropoff_location || '',
      price_amount: schedule.price_amount ?? '',
      price_currency: schedule.price_currency || 'JPY',
      service_type: schedule.service_type || 'transfer',
      vehicle_type: schedule.vehicle_type || '',
      driver_id: schedule.driver_id || '',
      customer_name: schedule.customer_name || '',
      customer_company: schedule.customer_company || '',
      customer_contact: schedule.customer_contact || '',
      customer_code: schedule.customer_code || '',
      flight_number: schedule.flight_number || '',
      passenger_count: schedule.passenger_count ?? '',
      luggage_count: schedule.luggage_count ?? '',
      infant_seat_count: schedule.infant_seat_count ?? 0,
      notes: schedule.notes || '',
      internal_notes: schedule.internal_notes || '',
      driver_collection_amount: schedule.driver_collection_amount ?? '',
      driver_collection_currency: schedule.driver_collection_currency || 'JPY',
    };
    setForm(snapshot);
    setInitialForm(snapshot);
    setRevision(null);
    if (schedule.source_type === 'dispatcher_extra') {
      fetchAuditLogs(true);
    }
  }, [schedule, fetchAuditLogs]);

  const handleSave = async () => {
    if (busy.current || revision == null) return;
    setError('');
    const validationError = validateScheduleForm(canViewPrice ? form : { ...form, price_amount: 0, price_currency: 'JPY', driver_collection_amount: '' });
    if (validationError) { setError(validationError); return; }
    const summary = criticalChangeSummary(initialForm, form, drivers);
    if (summary && !window.confirm(`确认以下修改？\n${summary}\n修改不会自动收款或退款。保存不代表司机已收到，请另行联系司机确认。`)) return;
    busy.current = true;
    setSaving(true);
    try {
      if (!(await confirmScheduleConflict(API_BASE_URL, getDispatcherToken(), {
        driver_id: form.driver_id, date: form.date, time: form.time, exclude_id: schedule.id,
      }))) return;
      const body = {
        date: form.date || undefined,
        time: form.time || undefined,
        pickup_location: form.pickup_location || undefined,
        dropoff_location: form.dropoff_location || undefined,
        price_amount: form.price_amount === '' ? undefined : parseFloat(form.price_amount),
        price_currency: form.price_currency || undefined,
        service_type: form.service_type || undefined,
        vehicle_type: form.vehicle_type || undefined,
        customer_name: form.customer_name || undefined,
        customer_company: form.customer_company || undefined,
        customer_contact: form.customer_contact || undefined,
        customer_code: form.customer_code || undefined,
        flight_number: form.flight_number || undefined,
        passenger_count: form.passenger_count ? parseInt(form.passenger_count) : undefined,
        luggage_count: form.luggage_count ? parseInt(form.luggage_count) : undefined,
        notes: form.notes !== undefined ? form.notes : undefined,
        internal_notes: form.internal_notes !== undefined ? form.internal_notes : undefined,
        driver_collection_amount: form.driver_collection_amount ? parseFloat(form.driver_collection_amount) : undefined,
        driver_collection_currency: form.driver_collection_currency || undefined,
      };
      OPTIONAL_TEXT_FIELDS.forEach(key => { body[key] = form[key] ?? ''; });
      COUNT_FIELDS.forEach(key => { body[key] = form[key] === '' || form[key] == null ? null : Number(form[key]); });
      body.driver_collection_amount = form.driver_collection_amount === '' || form.driver_collection_amount == null ? null : Number(form.driver_collection_amount);
      body.conflict_confirmed = true;
      body.expected_revision = revision;
      body.expected_updated_at = updatedAt;
      if (!canViewPrice) ['price_amount', 'price_currency', 'driver_collection_amount', 'driver_collection_currency'].forEach(key => delete body[key]);
      if (!canViewCustomer) ['customer_name', 'customer_company', 'customer_contact', 'customer_code'].forEach(key => delete body[key]);
      if (!canViewCustomer || !canViewPrice) ['notes', 'internal_notes'].forEach(key => delete body[key]);
      // Driver reassignment
      if (form.driver_id && form.driver_id !== schedule.driver_id) {
        body.driver_id = form.driver_id;
      }

      const res = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/${schedule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getDispatcherToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showOperationFeedback(await res.json());
        onSaved?.();
      } else {
        const data = await res.json();
        setError(data.detail || '保存失败');
      }
    } catch (err) {
      setError('保存失败');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (busy.current || revision == null || !window.confirm(cancelConfirmation({ ...schedule, ...initialForm }))) return;
    busy.current = true;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/${schedule.id}/cancel?expected_revision=${revision}${updatedAt ? `&expected_updated_at=${encodeURIComponent(updatedAt)}` : ''}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` },
      });
      if (res.ok) { showOperationFeedback(await res.json()); onSaved?.(); }
      else { const d = await res.json(); setError(d.detail || '取消失败'); }
    } catch { setError('取消失败'); }
    finally { busy.current = false; setSaving(false); }
  };

  const handleDelete = async () => {
    if (busy.current || revision == null || !window.confirm(cancelConfirmation({ ...schedule, ...initialForm }))) return;
    busy.current = true;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/${schedule.id}?expected_revision=${revision}${updatedAt ? `&expected_updated_at=${encodeURIComponent(updatedAt)}` : ''}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` },
      });
      if (res.ok) { showOperationFeedback(await res.json()); onSaved?.(); }
      else { const d = await res.json(); setError(d.detail || '删除失败'); }
    } catch { setError('删除失败'); }
    finally { busy.current = false; setSaving(false); }
  };

  const isOfficial = schedule.source_type === 'official';
  const sourceLabel = isOfficial ? '正式订单' : schedule.source_type === 'dispatcher_extra' ? '调度追加' : '司机追加';
  const serviceLabel = SERVICE_TYPES.find(s => s.value === (schedule.service_type || form.service_type))?.label || '-';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4" onClick={requestClose}>
      <div 
        className="dispatcher-form-modal bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[95vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 sm:p-4 border-b flex justify-between items-start bg-surface rounded-t-lg">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm sm:text-base font-semibold text-foreground">
                {canEdit ? '编辑订单' : '订单详情'}
              </h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                isOfficial ? 'premium-chip-success' :
                schedule.source_type === 'dispatcher_extra' ? 'premium-chip-warning' : 'premium-chip'
              }`}>{sourceLabel}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                form.service_type === 'charter' ? 'premium-chip-success' :
                'premium-chip'
              }`}>{serviceLabel}</span>
              {schedule.status === 'cancelled' && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium premium-chip-destructive">已取消</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 truncate">ID: {schedule.id?.slice(0, 8)}</p>
          </div>
          <button onClick={requestClose} disabled={saving} className="ml-2 w-7 h-7 flex items-center justify-center rounded hover:bg-muted">
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        {!isOfficial && (
          <div className="flex border-b bg-card px-3 pt-1">
            {[
              { key: 'detail', label: '详情' },
              { key: 'logs', label: '操作日志' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition ${
                  activeTab === tab.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-muted-foreground'
                }`}
                data-testid={`tab-${tab.key}`}
              >{tab.label}</button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {error && (
            <div className="premium-chip-destructive rounded-lg px-3 py-2 text-xs mb-3">{error}</div>
          )}
          {auditError && <button onClick={() => fetchAuditLogs(revision == null)} className="text-destructive">{auditError}，点击重试</button>}

          {activeTab === 'detail' && (
            <div className="space-y-4">
              {/* 行程信息 */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">行程信息</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="出发日期" field="date" type="date" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="出发时间" field="time" type="time" />
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="出发地点" field="pickup_location" placeholder="出发地" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="目的地" field="dropoff_location" placeholder="目的地" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="航班号" field="flight_number" placeholder="NH879" />
                  {canEdit ? (
                    <Field label="订单类型">
                      <select
                        value={form.service_type}
                        onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                        className="premium-input text-sm"
                        data-testid="edit-service_type"
                      >
                        {SERVICE_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <ReadOnly label="订单类型" value={serviceLabel} />
                  )}
                </div>
              </div>

              {/* 客户信息 */}
              {canViewCustomer && <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">客户信息</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="客人姓名" field="customer_name" placeholder="姓名" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="公司名" field="customer_company" placeholder="公司名" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="客户代码" field="customer_code" placeholder="YM-67" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="联系方式" field="customer_contact" placeholder="电话/邮箱" />
                </div>
              </div>}

              {/* 车辆信息 */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">车辆 & 乘客</h3>
                <div className="grid grid-cols-2 gap-3">
                  {canEdit ? (
                    <Field label="司机">
                      <select
                        value={form.driver_id}
                        onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                        className="premium-input text-sm"
                        data-testid="edit-driver_id"
                      >
                        <option value="">选择司机</option>
                        {(drivers || []).map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <ReadOnly label="司机" value={schedule.driver_name || drivers?.find(d => d.id === schedule.driver_id)?.name || '-'} />
                  )}
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="车辆类型" field="vehicle_type" placeholder="alphard" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="乘客人数" field="passenger_count" type="number" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="行李数量" field="luggage_count" type="number" />
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="婴儿座椅（每个¥2,000）" field="infant_seat_count" type="number" />
                </div>
              </div>

              {/* 金额 */}
              {canViewPrice && <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">金额</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="订单金额" field="price_amount" type="number" />
                  {canEdit ? (
                    <Field label="币种">
                      <select
                        value={form.price_currency}
                        onChange={(e) => setForm({ ...form, price_currency: e.target.value })}
                        className="premium-input text-sm"
                      >
                        <option value="JPY">JPY (日元)</option>
                        <option value="CNY">CNY (人民币)</option>
                      </select>
                    </Field>
                  ) : (
                    <ReadOnly label="币种" value={form.price_currency} />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Input form={form} setForm={setForm} canEdit={canEdit} label="司机代收金额" field="driver_collection_amount" type="number" />
                  {canEdit ? (
                    <Field label="代收币种">
                      <select
                        value={form.driver_collection_currency}
                        onChange={(e) => setForm({ ...form, driver_collection_currency: e.target.value })}
                        className="premium-input text-sm"
                      >
                        <option value="JPY">JPY (日元)</option>
                        <option value="CNY">CNY (人民币)</option>
                      </select>
                    </Field>
                  ) : (
                    <ReadOnly label="代收币种" value={form.driver_collection_currency || '-'} />
                  )}
                </div>
              </div>}

              {/* 备注 */}
              {canViewPrice && canViewCustomer && <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">备注</h3>
                {canEdit ? (
                  <>
                    <Field label="备注">
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="premium-input text-sm h-16 resize-none"
                        placeholder="备注信息"
                        data-testid="edit-notes"
                      />
                    </Field>
                    <div className="mt-3">
                      <Field label="内部备注（仅管理员/调度可见）">
                        <textarea
                          value={form.internal_notes}
                          onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                          className="premium-input text-sm h-16 resize-none"
                          placeholder="客人信息、特殊要求、调度备注、司机注意事项..."
                          data-testid="edit-internal_notes"
                        />
                      </Field>
                    </div>
                  </>
                ) : (
                  <>
                    <ReadOnly label="备注" value={form.notes} />
                    {form.internal_notes && (
                      <div className="mt-2 p-2 rounded">
                        <div className="premium-chip-warning mb-1">内部备注</div>
                        <p className="text-xs text-muted-foreground">{form.internal_notes}</p>
                      </div>
                    )}
                  </>
                )}
              </div>}

              {/* Meta info */}
              <div className="pt-3 border-t border-border-subtle">
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>创建人: {schedule.created_by_name || '-'}</div>
                  <div>创建时间: {schedule.created_at ? new Date(schedule.created_at).toLocaleString('zh-CN') : '-'}</div>
                  {schedule.updated_by_name && (
                    <>
                      <div>最近修改人: {schedule.updated_by_name}</div>
                      <div>修改时间: {schedule.updated_at ? new Date(schedule.updated_at).toLocaleString('zh-CN') : '-'}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-3">
              {auditError ? <button onClick={fetchAuditLogs} className="text-destructive">{auditError}</button> : auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8">暂无操作记录</p>
              ) : (
                auditLogs.map(log => (
                  <div key={log.id} className="border border-border-subtle rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-foreground">{log.operator_name || '未知账户'} <span className="text-xs text-muted-foreground">({log.operator_id})</span></span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString('zh-CN')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-medium">{log.action}</p>
                    {log.details && <p className="text-xs text-muted-foreground mt-0.5">{log.details}</p>}
                    {log.changes?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {log.changes.map((c, i) => (
                          <div key={i} className="text-xs bg-surface rounded px-2 py-1">
                            <span className="text-muted-foreground">{c.label}:</span>{' '}
                            <span className="text-destructive line-through">{c.old_value === '' || c.old_value == null ? '未填写' : c.old_value}</span>
                            {' → '}
                            <span className="text-success font-medium">{c.new_value === '' || c.new_value == null ? '已清空' : c.new_value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-surface flex justify-between items-center rounded-b-lg">
          <div className="flex gap-2">
            {canDelete && (
              confirmDelete ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-destructive">确定删除?</span>
                  <button onClick={handleDelete} className="px-2 py-1 bg-destructive text-destructive-foreground rounded-lg text-xs hover:bg-destructive/90">确定</button>
                  <button onClick={() => setConfirmDelete(false)} className="px-2 py-1 bg-muted text-muted-foreground rounded-lg text-xs">取消</button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-3 py-1.5 premium-chip-destructive rounded-lg text-xs"
                  data-testid="delete-schedule-btn"
                >删除</button>
              )
            )}
            {canCancel && (
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 bg-warning text-warning-foreground rounded-lg text-xs hover:bg-warning/90 transition"
                data-testid="cancel-schedule-btn"
              >取消排班</button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={requestClose}
              disabled={saving}
              className="px-4 py-1.5 bg-muted text-foreground rounded-lg text-xs hover:bg-surface transition"
            >关闭</button>
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={saving || revision == null}
                className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs hover:brightness-110 transition disabled:opacity-50"
                data-testid="save-schedule-btn"
              >{saving ? '保存中...' : '保存修改'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
